import type { Ayah } from '@app-types/KuranTypes'
import BottomPagination from '@components/shared/BottomPagination/BottomPagination'
import LoadingSpinner from '@components/shared/Loading/Loading'
import { PaginationAndSettingsRow } from '@components/shared/PaginationAndSettingsRow/PaginationAndSettingsRow'
import type { PageData } from '@components/shared/PageInfoRow/PageInfoRow'
import { useUserData } from '@contexts/UserDataContext'
import { useScrollToHashTarget } from '@hooks/useScrollToHashTarget'
import { getAllAyahIds } from 'helpers/kuran'
import dynamic from 'next/dynamic'
import { useMemo, useState } from 'react'
import ReaderContent from './ReaderContent'

const SoundPlayer = dynamic(
  () => import('@components/Kuran/KelimeMealliPage/SoundPlayer'),
  { ssr: false },
)

export type QuranReaderContainerProps = {
  page: number
  ayahs?: Ayah[]
  pageData?: PageData
  mobileReader?: boolean
  isContentReady?: boolean
}

const emptyAyahs: Ayah[] = []

/** Shared web/native Quran reader with a persistent dock and a single audio engine. */
export default function QuranReaderContainer({
  page,
  ayahs = emptyAyahs,
  pageData,
  mobileReader = false,
  isContentReady = true,
}: QuranReaderContainerProps) {
  const { userData } = useUserData()
  const [isAudioPanelOpen, setIsAudioPanelOpen] = useState(false)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const soundFiles = useMemo(() => getAllAyahIds(ayahs), [ayahs])
  const ready = isContentReady && Boolean(pageData)

  // Open links like /kuran/599#sure-100 at the selected surah, not the top of the page.
  useScrollToHashTarget(ready, `${page}:${userData.kuranType}`)

  /** Renders text in either normal or focus mode without mounting another audio player. */
  const renderReadingContent = () =>
    ready && pageData ? (
      <ReaderContent
        kind="quran"
        page={String(page)}
        ayahs={ayahs}
        pageData={pageData}
        mobileReader={mobileReader}
        showSoundPlayer={false}
      />
    ) : (
      <LoadingSpinner />
    )

  return (
    <div className="mx-auto mb-5">
      <PaginationAndSettingsRow
        min={0}
        max={604}
        type="kuran"
        mobileReader={mobileReader}
        autoScrollReady={ready}
        renderFocusContent={renderReadingContent}
        bottomDock={{
          isAudioPanelOpen,
          isAudioPlaying,
          onToggleAudioPanel: () => setIsAudioPanelOpen((open) => !open),
          audioPanel: (
            <SoundPlayer
              soundFiles={soundFiles}
              page={String(page)}
              variant="dock"
              isContentReady={ready}
              onClose={() => setIsAudioPanelOpen(false)}
              onPlaybackChange={setIsAudioPlaying}
            />
          ),
        }}
      />
      {renderReadingContent()}
      {ready && (
        <BottomPagination max={604} type="kuran" mobileReader={mobileReader} min={0} />
      )}
      {ready && userData.kuranType === 'hayrat' && (
        <p className="text-xs text-center italic">
          Hayrat Neşriyat Ahmet Hüsrev Hattı Kur’an-ı Kerimidir
        </p>
      )}
      {/* Leave enough scrollable room for the floating dock and the expanded audio row. */}
      <div
        aria-hidden="true"
        className={
          isAudioPanelOpen
            ? 'h-[calc(240px+env(safe-area-inset-bottom))]'
            : 'h-[calc(80px+env(safe-area-inset-bottom))]'
        }
      />
    </div>
  )
}
